import React from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import SEOHead from "../components/seo/SEOHead";
import BreadcrumbSchema from "../components/seo/BreadcrumbSchema";

const FAQ: React.FC = () => {
  const { isDarkMode } = useTheme();

  const faqs = [
    {
      question: "How do I register for programs?",
      answer: "You can register for programs by visiting our Programs page and selecting the programs that interest you. Complete the registration form with your details and payment information."
    },
    {
      question: "What payment methods do you accept?",
      // Neutral on purpose: this page is the same for every club, and which
      // cards a club takes is the club's to say, not ours (2026-11-02).
      answer: "Payment options are shown when you register or pay online. If you have a question about how to pay, please contact us."
    },
    {
      question: "Can I cancel my registration?",
      answer: "Cancellation policies vary by program. Please contact our support team for specific information about your program's cancellation policy."
    },
    {
      question: "How do I update my profile information?",
      answer: "You can update your profile information by logging into your account and visiting the Profile section."
    },
    {
      question: "What if I need to reschedule my program sessions?",
      answer: "Please contact our support team as soon as possible if you need to reschedule. We'll do our best to accommodate your request based on availability."
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SEOHead title="FAQ" description="Find answers to frequently asked questions about our programs, registration, and services." />
      <BreadcrumbSchema items={[{ name: "FAQ", path: "/faq" }]} />
      {/* No FAQPage JSON-LD: these answers are the platform's, identical for
          every club, and none of them is something the club configured.
          Structured data would present them to search engines as the club's
          own answers (2026-11-02). */}
      <Navbar />

      <div className="flex-1 py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Frequently Asked Questions
            </h1>
            <p className={`text-base sm:text-lg leading-relaxed max-w-3xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Find answers to common questions about our programs, registration, and services.
            </p>
          </div>

          <div className={`rounded-lg shadow-md p-6 sm:p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="space-y-6 sm:space-y-8">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 sm:pb-8 last:border-b-0 last:pb-0">
                  <h3 className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {faq.question}
                  </h3>
                  <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>

            <div className={`mt-8 sm:mt-12 p-4 sm:p-6 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h3 className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Still have questions?
              </h3>
              <p className={`text-sm sm:text-base mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Can't find the answer you're looking for? Our support team is here to help.
              </p>
              <a
                href={`/${window.location.pathname.split('/')[1]}/contact`}
                className="inline-block px-6 py-3 sm:px-8 sm:py-4 bg-[var(--cc-action)] text-[var(--cc-action-text)] font-semibold rounded-lg hover:bg-[var(--cc-action-hover)] transition-colors duration-200 text-sm sm:text-base"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
